/**
 * Field visibility configuration per service category.
 *
 * Category names must match exactly what appears in cscCustomCategories
 * (seeded from seedMockCategories.js).
 *
 * Flags control which optional sections appear on the Add-Service form:
 *   dates          – Start Date & End Date
 *   examDates      – Exam Date & Admit Card Date
 *   qualifications – Qualification checkboxes
 *   age            – Min / Max Age fields
 */

/* ── Presets ─────────────────────────────────────────────── */
const ALL    = { dates: true,  examDates: true,  qualifications: true,  age: true  };
const NONE   = { dates: false, examDates: false, qualifications: false, age: false };
const AGE    = { dates: false, examDates: false, qualifications: false, age: true  };
const DATES  = { dates: true,  examDates: false, qualifications: false, age: false };
const D_AGE  = { dates: true,  examDates: false, qualifications: false, age: true  };
const D_Q_A  = { dates: true,  examDates: false, qualifications: true,  age: true  };

const SERVICE_FIELD_CONFIG = {

  // ── Identity & Document Services ─────────────────────────
  'Aadhaar Enrollment Services':        NONE,
  'Aadhaar Update Services':            NONE,
  'PAN Card Services':                  NONE,
  'Voter ID Services':                  AGE,   // age 18+
  'Passport Assistance':                AGE,   // minor/adult distinction
  'Driving License Services':           AGE,   // age 16/18+
  'Ration Card Services':               NONE,
  'Birth Certificate Services':         NONE,
  'Death Certificate Services':         NONE,
  'Marriage Certificate Services':      NONE,
  'Caste Certificate (OBC)':            NONE,
  'Caste Certificate (SC)':             NONE,
  'Caste Certificate (ST)':             NONE,
  'Income Certificate Services':        NONE,
  'Domicile Certificate':               NONE,
  'Character Certificate':              NONE,
  'Senior Citizen Certificate':         AGE,   // age 60+
  'BPL Certificate Services':           NONE,
  'Migration Certificate':              NONE,
  'NOC Certificate Services':           NONE,
  'Disability Certificate Services':    NONE,
  'Document Attestation':               NONE,
  'Bonafide Certificate':               NONE,

  // ── Banking & Financial Services ─────────────────────────
  'Jan Dhan Account Opening':           NONE,
  'KYC Update Services':                NONE,
  'Mudra Loan Services':                NONE,
  'PM SVANidhi Services':               NONE,
  'AEPS Banking Services':              NONE,
  'Money Transfer Services':            NONE,
  'Cash Withdrawal Services':           NONE,

  // ── Insurance Services ───────────────────────────────────
  'PM Jeevan Jyoti Bima':              AGE,   // age 18-50
  'PM Suraksha Bima':                   AGE,   // age 18-70
  'Fasal Bima Services':                DATES, // crop season dates
  'Life Insurance Premium':             NONE,  // just payment
  'Health Insurance Services':          AGE,
  'Vehicle Insurance Services':         NONE,  // just payment
  'Atal Pension Yojana':                AGE,   // age 18-40
  'NPS Services':                       AGE,   // age 18-65

  // ── Utility Bill Payments ────────────────────────────────
  'Electricity Bill Payment':           DATES, // billing period / due date
  'Water Bill Payment':                 DATES,
  'Gas Bill Payment':                   DATES,
  'Property Tax Services':              DATES,

  // ── Telecom & Internet ───────────────────────────────────
  'Mobile Recharge Services':           NONE,
  'DTH and Cable Services':             NONE,
  'Broadband and Internet':             NONE,

  // ── Travel & Ticketing ───────────────────────────────────
  'Train Ticket Booking':               NONE,
  'Bus Ticket Booking':                 NONE,
  'Flight Ticket Booking':              NONE,

  // ── Social Welfare Schemes ───────────────────────────────
  'PM Awas Yojana Services':            D_AGE, // application window + age
  'PM Ujjwala Yojana':                  D_AGE,
  'Widow Pension Services':             D_AGE,
  'Old Age Pension Services':           D_AGE, // age 60+
  'Disability Pension Services':        D_AGE,
  'PM Scholarship Services':            D_Q_A, // qualification + age + deadline

  // ── Agriculture Services ─────────────────────────────────
  'PM Kisan Services':                  DATES, // registration window
  'Soil Health Card':                   NONE,
  'Kisan Credit Card':                  NONE,
  'Agriculture Insurance':              DATES, // crop season
  'Fertilizer Subsidy Services':        DATES,

  // ── Employment & Labour ──────────────────────────────────
  'MNREGA Services':                    AGE,   // age 18+
  'Labour Registration':                AGE,   // e-Shram age 16-59
  'Job Portal Registration':            NONE,
  'EPF and UAN Services':               NONE,
  'Skill Development':                  D_Q_A, // qualification + age + enrollment dates

  // ── Education Services ───────────────────────────────────
  'Educational Assistance':             NONE,
  'Scholarship Application':            D_Q_A, // qualification + age + deadline

  // ── Health Services ──────────────────────────────────────
  'ABHA Health ID Services':            NONE,
  'Immunization Certificate':           NONE,
  'PM Matru Vandana Services':          NONE,

  // ── Tax Services ─────────────────────────────────────────
  'Income Tax Filing':                  DATES, // filing deadline
  'GST Registration Services':          NONE,
  'GST Return Filing':                  DATES, // filing deadline

  // ── Business Registration ────────────────────────────────
  'Udyam Registration':                 NONE,
  'FSSAI License Services':             NONE,
  'Trade License Application':          NONE,
  'Shop Establishment Registration':    NONE,

  // ── Land & Property Services ─────────────────────────────
  'Land Record Services':               NONE,
  'Property Mutation Services':         NONE,
  'Encumbrance Certificate':            NONE,

  // ── Digital & e-Gov Services ─────────────────────────────
  'DigiLocker Services':                NONE,
  'UMANG Services':                     NONE,
  'E-Governance Services':              NONE,
  'e-Sign Services':                    NONE,

  // ── Pension, Vehicle, Legal, Misc ────────────────────────
  'Pension Verification Services':      AGE,   // age for pension eligibility
  'Vehicle Registration Services':      NONE,
  'FastTag Services':                   NONE,
  'Legal Documentation Services':       NONE,
  'RTI Services':                       NONE,
  'Voter List Services':                NONE,

  // ── UPSC Exams ───────────────────────────────────────────
  'UPSC Civil Services':                ALL,
  'UPSC CAPF Recruitment':              ALL,
  'UPSC IFS Recruitment':               ALL,
  'UPSC CMS Recruitment':               ALL,
  'UPSC IES Recruitment':               ALL,
  'UPSC NDA Recruitment':               ALL,
  'UPSC CDS Recruitment':               ALL,
  'UPSC LDCE Recruitment':              ALL,

  // ── SSC Exams ────────────────────────────────────────────
  'SSC CGL Recruitment':                ALL,
  'SSC CHSL Recruitment':               ALL,
  'SSC MTS Recruitment':                ALL,
  'SSC GD Constable Recruitment':       ALL,
  'SSC CPO Recruitment':                ALL,
  'SSC JE Recruitment':                 ALL,
  'SSC Stenographer Recruitment':       ALL,
  'SSC Selection Post':                 ALL,

  // ── Railway Recruitment ──────────────────────────────────
  'RRB NTPC Recruitment':               ALL,
  'RRB Group D Recruitment':            ALL,
  'RRB ALP Recruitment':                ALL,
  'RRB JE Recruitment':                 ALL,
  'RRB Paramedical Recruitment':        ALL,
  'RPF Constable Recruitment':          ALL,
  'RPF Sub Inspector Recruitment':      ALL,

  // ── Banking Recruitment ──────────────────────────────────
  'IBPS PO Recruitment':                ALL,
  'IBPS Clerk Recruitment':             ALL,
  'IBPS SO Recruitment':                ALL,
  'IBPS RRB PO Recruitment':            ALL,
  'IBPS RRB Clerk Recruitment':         ALL,
  'SBI PO Recruitment':                 ALL,
  'SBI Clerk Recruitment':              ALL,
  'RBI Grade B Recruitment':            ALL,
  'RBI Assistant Recruitment':          ALL,
  'NABARD Grade A Recruitment':         ALL,

  // ── Insurance Recruitment ────────────────────────────────
  'LIC AAO Recruitment':                ALL,
  'LIC HFL Recruitment':                ALL,
  'NIACL Recruitment':                  ALL,
  'OICL Recruitment':                   ALL,
  'GIC Recruitment':                    ALL,

  // ── Defence & Paramilitary ───────────────────────────────
  'Indian Army Recruitment':            ALL,
  'Indian Navy Recruitment':            ALL,
  'Indian Air Force Recruitment':       ALL,
  'AFCAT Recruitment':                  ALL,
  'Agniveer Army Recruitment':          ALL,
  'Agniveer Navy Recruitment':          ALL,
  'Agniveer Air Force Recruitment':     ALL,
  'Coast Guard Recruitment':            ALL,
  'Territorial Army Recruitment':       ALL,
  'Assam Rifles Recruitment':           ALL,
  'MES Recruitment':                    ALL,
  'BRO Recruitment':                    ALL,

  // ── Central Paramilitary Forces ──────────────────────────
  'CRPF Constable Recruitment':         ALL,
  'BSF Constable Recruitment':          ALL,
  'CISF Constable Recruitment':         ALL,
  'CISF HC ASI Recruitment':            ALL,
  'ITBP Constable Recruitment':         ALL,
  'SSB Constable Recruitment':          ALL,
  'Delhi Police Constable':             ALL,
  'IB ACIO Recruitment':                ALL,

  // ── Central Teaching Recruitment ─────────────────────────
  'KVS Teacher Recruitment':            ALL,
  'NVS Teacher Recruitment':            ALL,
  'CTET Exam Services':                 ALL,
  'DSSSB Recruitment':                  ALL,

  // ── Central Health Recruitment ───────────────────────────
  'AIIMS Recruitment':                  ALL,
  'ESIC Recruitment':                   ALL,
  'NHM Recruitment':                    ALL,
  'PGIMER Recruitment':                 ALL,

  // ── PSU & Govt Enterprise Recruitment ────────────────────
  'FCI Recruitment':                    ALL,
  'DRDO Recruitment':                   ALL,
  'ISRO Recruitment':                   ALL,
  'HAL Recruitment':                    ALL,
  'BEL Recruitment':                    ALL,
  'ONGC Recruitment':                   ALL,
  'BHEL Recruitment':                   ALL,
  'NTPC Recruitment':                   ALL,
  'Power Grid Recruitment':             ALL,
  'IOCL Recruitment':                   ALL,
  'HPCL Recruitment':                   ALL,
  'BPCL Recruitment':                   ALL,
  'GAIL Recruitment':                   ALL,
  'SAIL Recruitment':                   ALL,
  'Coal India Recruitment':             ALL,
  'AAI Recruitment':                    ALL,
  'DMRC Recruitment':                   ALL,
  'BSNL Recruitment':                   ALL,
  'India Post Recruitment':             ALL,
  'India Post GDS Recruitment':         ALL,
  'CWC Recruitment':                    ALL,
  'High Court Recruitment':             ALL,
  'Supreme Court Recruitment':          ALL,
  'Income Tax Recruitment':             ALL,
  'CAG Recruitment':                    ALL,

  // ── State PSC Exams ──────────────────────────────────────
  'BPSC Recruitment':                   ALL,
  'UPPSC Recruitment':                  ALL,
  'MPSC Recruitment':                   ALL,
  'MPPSC Recruitment':                  ALL,
  'RPSC Recruitment':                   ALL,
  'TNPSC Recruitment':                  ALL,
  'KPSC Recruitment':                   ALL,
  'APPSC Recruitment':                  ALL,
  'TSPSC Recruitment':                  ALL,
  'GPSC Recruitment':                   ALL,
  'HPSC Recruitment':                   ALL,
  'WBPSC Recruitment':                  ALL,
  'OPSC Recruitment':                   ALL,
  'JPSC Recruitment':                   ALL,
  'CGPSC Recruitment':                  ALL,
  'PPSC Recruitment':                   ALL,
  'UKPSC Recruitment':                  ALL,
  'APSC Recruitment':                   ALL,
  'JKPSC Recruitment':                  ALL,
  'KERALAPSC Recruitment':              ALL,
  'HPPSC Recruitment':                  ALL,

  // ── State Level Government Jobs ──────────────────────────
  'State PSC Services':                 ALL,
  'State Police Constable':             ALL,
  'State Sub Inspector Recruitment':    ALL,
  'State TET Services':                 ALL,
  'Primary Teacher Recruitment':        ALL,
  'Secondary Teacher Recruitment':      ALL,
  'State Revenue Services':             ALL,
  'State Forest Guard Recruitment':     ALL,
  'Anganwadi Recruitment':              ALL,
  'Gram Panchayat Recruitment':         ALL,
  'State Health Worker Recruitment':    ALL,
  'State Electricity JE Recruitment':   ALL,
  'State PWD JE Recruitment':           ALL,
  'District Court Recruitment':         ALL,
  'State Agriculture Recruitment':      ALL,
  'State Fisheries Recruitment':        ALL,
  'State Transport Recruitment':        ALL,
  'State Municipal Recruitment':        ALL,
  'State Cooperative Recruitment':      ALL,
  'Home Guard Recruitment':             ALL,
  'State Jail Department':              ALL,
  'State Fire Services':                ALL,

  // ── Engineering Entrance Exams ───────────────────────────
  'JEE Main Exam Services':             ALL,
  'JEE Advanced Exam Services':         ALL,
  'BITSAT Exam Services':               ALL,
  'VITEEE Exam Services':               ALL,
  'SRMJEEE Exam Services':              ALL,
  'MHT CET Services':                   ALL,
  'KCET Exam Services':                 ALL,
  'AP EAMCET Services':                 ALL,
  'TS EAMCET Services':                 ALL,
  'WBJEE Exam Services':                ALL,
  'UPCET Exam Services':                ALL,
  'GUJCET Exam Services':               ALL,
  'KEAM Exam Services':                 ALL,
  'OJEE Exam Services':                 ALL,
  'COMEDK Exam Services':               ALL,

  // ── Medical Entrance Exams ───────────────────────────────
  'NEET UG Exam Services':              ALL,
  'NEET PG Exam Services':              ALL,
  'AIAPGET Services':                   ALL,

  // ── Management Entrance Exams ────────────────────────────
  'CAT Exam Services':                  ALL,
  'MAT Exam Services':                  ALL,
  'XAT Exam Services':                  ALL,
  'CMAT Exam Services':                 ALL,
  'SNAP Exam Services':                 ALL,
  'IPMAT Exam Services':                ALL,
  'JIPMAT Exam Services':               ALL,
  'NMAT Exam Services':                 ALL,

  // ── Law Entrance Exams ───────────────────────────────────
  'CLAT Exam Services':                 ALL,
  'AILET Exam Services':                ALL,
  'LSAT India Services':                ALL,

  // ── Research & Teaching Eligibility ──────────────────────
  'UGC NET JRF Services':               ALL,
  'CSIR NET Services':                  ALL,
  'GATE Exam Services':                 ALL,
  'SET SLET Services':                  ALL,

  // ── Design & Architecture Exams ──────────────────────────
  'NIFT Entrance Services':             ALL,
  'NID DAT Services':                   ALL,
  'NATA Exam Services':                 ALL,
  'UCEED Exam Services':                ALL,
  'CEED Exam Services':                 ALL,

  // ── School & University Entrance ─────────────────────────
  'CUET UG Services':                   ALL,
  'CUET PG Services':                   ALL,
  'JNVST Services':                     ALL,
  'AISSEE Services':                    ALL,
  'RMS Exam Services':                  ALL,

  // ── Catch-all ────────────────────────────────────────────
  'Others':                             ALL,
};

/* ── Keywords that identify exam / recruitment categories ── */
const EXAM_KEYWORDS = ['recruitment', 'exam', 'entrance', 'bharti'];

/**
 * Returns the field-visibility config for a given category name.
 *
 * 1. Exact match in the config map above
 * 2. Keyword fallback — if the name contains exam/recruitment keywords → ALL fields
 * 3. Unknown / custom categories → ALL fields (safe default)
 */
export const getFieldConfig = (categoryName) => {
  if (!categoryName || categoryName === 'Other') return ALL;

  // Exact match
  if (SERVICE_FIELD_CONFIG[categoryName]) return SERVICE_FIELD_CONFIG[categoryName];

  // Keyword-based detection for new exam/recruitment categories
  const lower = categoryName.toLowerCase();
  if (EXAM_KEYWORDS.some((kw) => lower.includes(kw))) return ALL;

  // Unknown / custom — show all fields to be safe
  return ALL;
};

export default SERVICE_FIELD_CONFIG;
